import type { ComponentLike } from '@glint/template'
import NeedsAttentionWidget from 'teacher-ui-client/components/teacher-home/widgets/needs-attention'
import TeacherHomeUpcomingAssignments from 'teacher-ui-client/components/teacher-home/widgets/upcoming-assignments'
import QuickActionsWidget from 'teacher-ui-client/components/teacher-home/widgets/quick-actions'
import ProgramLinksWidget from 'teacher-ui-client/components/teacher-home/widgets/program-links'
import RecentActivityWidget from 'teacher-ui-client/components/teacher-home/widgets/recent-activity'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

export type TeacherHomeWidgetId =
  | 'upcoming-assignments'
  | 'at-risk-students'
  | 'quick-actions'
  | 'program-links'
  | 'recent-activity'
  | 'needs-attention'

export type TeacherHomeWidgetSize = 'wide' | 'standard' | 'narrow'

type TeacherHomeWidgetComponent = ComponentLike<unknown>

export interface PartialTeacherHomePreferences {
  teacherHomeEnabled?: boolean
  widgets?: MaybeStoredWidget[]
}

export interface TeacherHomeWidgetDefinition {
  id: TeacherHomeWidgetId
  label: string
  size: TeacherHomeWidgetSize
  component: TeacherHomeWidgetComponent
  subscriptionType?: SubscriptionType
}

export interface TeacherHomeStoredWidget {
  id: TeacherHomeWidgetId
  enabled: boolean
}

export interface TeacherHomePreferences {
  teacherHomeEnabled: boolean
  widgets: TeacherHomeStoredWidget[]
}

export interface TeacherHomeWidgetViewModel extends TeacherHomeWidgetDefinition, TeacherHomeStoredWidget {
  gridClass: string
}

type MaybeStoredWidget = Omit<Partial<TeacherHomeStoredWidget>, 'id'> & { id: string; order?: number }

export const WIDGET_REGISTRY: TeacherHomeWidgetDefinition[] = [
  {
    id: 'upcoming-assignments',
    label: 'Upcoming Assignments',
    size: 'standard',
    component: TeacherHomeUpcomingAssignments,
  },
  {
    id: 'needs-attention',
    label: 'Needs Attention',
    size: 'standard',
    component: NeedsAttentionWidget,
    subscriptionType: SubscriptionType.Reading,
  },
  {
    id: 'quick-actions',
    label: 'Quick Actions',
    size: 'standard',
    component: QuickActionsWidget,
  },
  {
    id: 'program-links',
    label: 'Program Access',
    size: 'standard',
    component: ProgramLinksWidget,
  },
  {
    id: 'recent-activity',
    label: 'Recent Activity',
    size: 'standard',
    component: RecentActivityWidget,
  },
  // {
  //   id: 'classes-at-a-glance',
  //   label: 'Classes at a Glance',
  //   size: 'standard',
  //   component: ClassesAtAGlanceWidget,
  // },
]

const WIDGET_REGISTRY_BY_ID = new Map(WIDGET_REGISTRY.map((widget) => [widget.id, widget]))
export const WIDGET_IDS = WIDGET_REGISTRY.map(({ id }) => id)
const WIDGET_ID_SET = new Set<TeacherHomeWidgetId>(WIDGET_IDS)

export function isTeacherHomeWidgetId(value: string): value is TeacherHomeWidgetId {
  return WIDGET_ID_SET.has(value as TeacherHomeWidgetId)
}

export function gridClassForWidgetSize(size: TeacherHomeWidgetSize) {
  switch (size) {
    case 'wide':
      return 'col-span-1 sm:col-span-2 xl:col-span-2'
    case 'standard':
      return 'col-span-1'
    case 'narrow':
      return 'col-span-1'
  }
}

export function buildDefaultTeacherHomePreferences(): TeacherHomePreferences {
  return {
    teacherHomeEnabled: true,
    widgets: WIDGET_IDS.map((id) => ({ id, enabled: true })),
  }
}

export function cloneTeacherHomePreferences(preferences: TeacherHomePreferences): TeacherHomePreferences {
  return {
    teacherHomeEnabled: preferences.teacherHomeEnabled,
    widgets: preferences.widgets.map((widget) => ({ ...widget })),
  }
}

export function normalizeTeacherHomePreferences(
  maybePreferences?: PartialTeacherHomePreferences | null,
): TeacherHomePreferences {
  if (!maybePreferences) {
    return buildDefaultTeacherHomePreferences()
  }

  const storedWidgets = (maybePreferences.widgets ?? []).filter(
    (widget): widget is MaybeStoredWidget => Boolean(widget?.id) && isTeacherHomeWidgetId(widget.id),
  )
  const orderedStoredWidgets = storedWidgets.some((widget) => typeof widget.order === 'number')
    ? storedWidgets
        .slice()
        .sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER))
    : storedWidgets
  const storedWidgetsById = new Map(storedWidgets.map((widget) => [widget.id as TeacherHomeWidgetId, widget]))
  const orderedIds = [...new Set(orderedStoredWidgets.map(({ id }) => id as TeacherHomeWidgetId).concat(WIDGET_IDS))]

  return {
    teacherHomeEnabled: maybePreferences.teacherHomeEnabled ?? true,
    widgets: orderedIds.map((id) => ({
      id,
      enabled: storedWidgetsById.get(id)?.enabled ?? true,
    })),
  }
}

export function getTeacherHomeWidgetDefinition(id: TeacherHomeWidgetId): TeacherHomeWidgetDefinition {
  const widget = WIDGET_REGISTRY_BY_ID.get(id)

  if (!widget) {
    throw new Error(`Unknown teacher home widget: ${id}`)
  }

  return widget
}

export function isTeacherHomeWidgetAvailable(
  widget: Pick<TeacherHomeWidgetDefinition, 'subscriptionType'>,
  currentSubscriptionType?: SubscriptionType,
) {
  return !widget.subscriptionType || !currentSubscriptionType || widget.subscriptionType === currentSubscriptionType
}

export function buildTeacherHomeWidgetViewModels(
  preferences: TeacherHomePreferences,
  currentSubscriptionType?: SubscriptionType,
): TeacherHomeWidgetViewModel[] {
  return normalizeTeacherHomePreferences(preferences).widgets.flatMap((widgetPreference) => {
    const widgetDefinition = getTeacherHomeWidgetDefinition(widgetPreference.id)

    if (!isTeacherHomeWidgetAvailable(widgetDefinition, currentSubscriptionType)) {
      return []
    }

    return {
      ...widgetDefinition,
      ...widgetPreference,
      gridClass: gridClassForWidgetSize(widgetDefinition.size),
    }
  })
}
